import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useBreadcrumbs } from '../../../app/breadcrumb-provider';
import { fetchShipmentAlerts, fetchShipmentList } from '../api';

import {
  AlertCardData,
  ShipmentFilters as FilterTypes,
  ShipmentStatus,
  ShipmentItem,
  ArrivalBucket,
  TripType,
  LoadType,
} from '../../../types/shipment';

import { DataTable, AlertCard, AlertFilter as DataTableAlertFilter } from 'components/ui/data-table';
import { cpShipmentColumns } from './columns';
import { format, startOfDay, endOfDay } from 'date-fns';

interface DateRange {
  from?: Date;
  to?: Date;
}

type PageAlertFilter = {
  arrival?: ArrivalBucket;
  trip_type?: TripType;
  load_type?: LoadType;
  pod?: boolean;
  freight_invoice?: boolean;
};

type ShipmentsPageProps = {
  status: ShipmentStatus | 'all';
};

const mapToDataTableFilter = (filters: PageAlertFilter): DataTableAlertFilter => ({
  arrival: filters.arrival,
  trip_type: filters.trip_type,
  load_type: filters.load_type,
  pod: filters.pod,
  freight_invoice: filters.freight_invoice,
});

const mapBucketIdToAlertFilter = (cardId: string, bucketId: string) => {
  switch (cardId) {
    case 'arrival':
      return { arrival: bucketId as ArrivalBucket };
    case 'trip_type':
      return { trip_type: bucketId as TripType };
    case 'load_type':
      return { load_type: bucketId as LoadType };
    default:
      return {};
  }
};

const mapAlertCardsToDataTable = (cards: AlertCardData[]): AlertCard[] =>
  cards.map(card => {
    let style: 'positive' | 'negative' | 'neutral' | 'total' = 'neutral';
    if (card.id === 'arrival') style = 'positive';
    else if (card.id === 'trip_type' || card.id === 'load_type') style = 'total';

    return {
      id: card.id,
      title: card.label,
      type: card.buckets?.length ? 'bucketed' : 'single',
      style,
      count: card.count,
      buckets: (card.buckets ?? []).map(bucket => ({
        id: bucket.id,
        label: bucket.label,
        count: bucket.count,
        filters: mapBucketIdToAlertFilter(card.id, bucket.id),
      })),
    };
  });

const buildKey = (obj: unknown) => JSON.stringify(obj);

const ShipmentsPage: React.FC<ShipmentsPageProps> = ({ status }) => {
  const { setBreadcrumbs, setHeaderBorder } = useBreadcrumbs();

  const didMountRef = useRef(false);
  const lastShipmentKeyRef = useRef<string | null>(null);
  const lastAlertKeyRef = useRef<string | null>(null);

  const [globalFilter, setGlobalFilter] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({});

  const [onHold, setOnHold] = useState<boolean>(false);

  const [shipments, setShipments] = useState<ShipmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [sorting, setSorting] = useState([{ id: 'cn_date', desc: true }]);

  const [alerts, setAlerts] = useState<AlertCardData[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(false);

  const [activeAlertFilters, setActiveAlertFilters] = useState<PageAlertFilter>({});

  useEffect(() => {
    const titleCaseStatus = status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, char => char.toUpperCase());

    setBreadcrumbs([
      { label: 'Shipments', href: '/shipments' },
      { label: titleCaseStatus },
    ]);

    setHeaderBorder(false);
    return () => setHeaderBorder(true);
  }, [status]);

  useEffect(() => {
    if (!didMountRef.current) return;

    setActiveAlertFilters({});
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [status]);

  useEffect(() => {
    const key = buildKey({
      status,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      globalFilter,
      dateRange,
      sorting,
      activeAlertFilters,
      onHold,
    });

    if (lastShipmentKeyRef.current === key) return;
    lastShipmentKeyRef.current = key;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await fetchShipmentList({
          page: pagination.pageIndex + 1,
          per_page: pagination.pageSize,
          status,
          search: globalFilter || undefined,
          from_date: dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss') : undefined,
          to_date: dateRange.to ? format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss') : undefined,
          ...activeAlertFilters,
          sort_by: sorting[0]?.id || 'cn_date',
          sort_order: sorting[0]?.desc ? 'desc' : 'asc',
          on_hold: onHold || undefined,
        });

        setShipments(res.items || []);
        setTotalCount(res.total_items || 0);
        setPageCount(res.total_pages || 0);
      } catch {
        setShipments([]);
        setTotalCount(0);
        setPageCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [status, pagination.pageIndex, pagination.pageSize, globalFilter, dateRange, sorting, activeAlertFilters, onHold]);

  useEffect(() => {
    if (status !== 'in_transit') {
      setAlerts([]);
      return;
    }

    const key = buildKey({
      status,
      globalFilter,
      dateRange,
      activeAlertFilters,
    });

    if (lastAlertKeyRef.current === key) return;
    lastAlertKeyRef.current = key;

    setAlertsLoading(true);

    fetchShipmentAlerts({
      status,
      search: globalFilter || undefined,
      from_date: dateRange.from ? format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss') : undefined,
      to_date: dateRange.to ? format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss') : undefined,
      ...activeAlertFilters,
    })
      .then(res => setAlerts(res.cards || []))
      .catch(() => setAlerts([]))
      .finally(() => setAlertsLoading(false));
  }, [status, globalFilter, dateRange, activeAlertFilters]);

  useEffect(() => {
    didMountRef.current = true;
  }, []);

  const resetPage = () =>
    setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));

  const handleAlertFilterChange = (filters: PageAlertFilter) => {
    setActiveAlertFilters(filters);
    resetPage();
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
    resetPage();
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range || {});
    resetPage();
  };

  const columnsWithRefresh = cpShipmentColumns(status, () => {
    lastShipmentKeyRef.current = null;
  });

  const handleOnHoldChange = (value: boolean) => {
    setOnHold(value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <div className="h-full w-full overflow-hidden flex flex-col">
      <div className="flex-1 min-h-0 overflow-hidden">
        <DataTable
          data={shipments}
          columns={columnsWithRefresh}
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={s => { setSorting(s); resetPage(); }}
          totalCount={totalCount}
          pageCount={pageCount}
          globalFilter={globalFilter}
          onGlobalFilterChange={handleGlobalFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          alertCards={mapAlertCardsToDataTable(alerts)}
          isLoadingAlerts={alertsLoading}
          onHold={onHold}
          onOnHoldChange={handleOnHoldChange}
          activeAlertFilters={mapToDataTableFilter(activeAlertFilters)}
          onAlertFilterChange={handleAlertFilterChange}
          currentTab={status}
        />

        {!loading && shipments.length === 0 && (
          <div className="py-4 text-center text-gray-500">No shipments found.</div>
        )}
      </div>
    </div>
  );
};

export default ShipmentsPage;
